import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { LOGO_URL } from "../data/mock";
import { trackEvent } from "../lib/analytics";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentInfo(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("success");

          // Fire GA4 `purchase` event once per session (guard against reload duplicates)
          const firedKey = `purchase_fired_${sessionId}`;
          if (!sessionStorage.getItem(firedKey)) {
            const value = res.data.amount_total ? res.data.amount_total / 100 : 99;
            const currency = (res.data.currency || "aed").toUpperCase();
            trackEvent("purchase", {
              transaction_id: sessionId,
              value,
              currency,
              items: [
                {
                  item_id: "steamers_monthly",
                  item_name: "Steamers Monthly Bin Cleaning",
                  price: value,
                  quantity: 1,
                },
              ],
            });
            sessionStorage.setItem(firedKey, "1");
          }
        } else if (res.data.payment_status === "unpaid") {
          setStatus("pending");
        } else {
          setStatus("error");
        }
      } catch (err) {
        setStatus("error");
      }
    };

    checkStatus();
  }, []);

  return (
    <div className="min-h-screen bg-[#f5fcf9] flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-3xl shadow-xl shadow-[#16C79A]/5 border border-[#e0f5ef] p-10">
          <img src={LOGO_URL} alt="Steamers" className="h-20 mx-auto mb-8" />

          {status === "loading" && (
            <>
              <Loader2 size={48} className="animate-spin text-[#16C79A] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#0C2D48] mb-2">
                Confirming your payment...
              </h2>
              <p className="text-slate-500">Please wait a moment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#16C79A]/15 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={44} className="text-[#16C79A]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0C2D48] mb-3">
                Payment Successful!
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                Thank you for subscribing to Steamers! We'll be in touch within
                24 hours to arrange your first clean.
              </p>
              {paymentInfo && (
                <div className="bg-[#f5fcf9] rounded-xl p-4 mb-8 text-left">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Amount</span>
                    <span className="font-semibold text-[#0C2D48]">
                      {(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Status</span>
                    <span className="font-semibold text-[#16C79A]">Paid</span>
                  </div>
                </div>
              )}
              <Button
                onClick={() => (window.location.href = "/")}
                className="bg-[#16C79A] hover:bg-[#12a87e] text-white font-semibold px-8 py-3 rounded-full transition-all duration-300"
              >
                Back to Home
              </Button>
            </>
          )}

          {status === "pending" && (
            <>
              <Loader2 size={48} className="animate-spin text-[#16C79A] mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#0C2D48] mb-3">
                Payment Processing
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                Your payment is still being processed. Please check back shortly.
              </p>
              <Button
                onClick={() => window.location.reload()}
                className="bg-[#16C79A] hover:bg-[#12a87e] text-white font-semibold px-8 py-3 rounded-full transition-all duration-300"
              >
                Refresh
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <XCircle size={44} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-[#0C2D48] mb-3">
                Something went wrong
              </h2>
              <p className="text-slate-500 leading-relaxed mb-8">
                We couldn't confirm your payment. Please contact us on WhatsApp
                if you need help.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => (window.location.href = "/")}
                  className="bg-[#16C79A] hover:bg-[#12a87e] text-white font-semibold px-8 py-3 rounded-full transition-all duration-300"
                >
                  Back to Home
                </Button>
                <a
                  href="https://wa.me/971502856909"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#16C79A] hover:underline"
                >
                  Contact us on WhatsApp
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
